import React, { useState } from "react";

// Collapsible tree for arbitrary JSON. Children only mount when a node is
// open, and long arrays/objects are capped, so multi-megabyte documents stay
// renderable. Shared by the Files parsed-output viewer and the API Explorer.

const JSON_TREE_CHILD_LIMIT = 200;

export function JsonPrimitive({ value }: { value: unknown }) {
  if (value === null) return <span className="text-kfaint italic">null</span>;
  switch (typeof value) {
    case "string":  return <span className="text-emerald-700 break-all">"{value}"</span>;
    case "number":  return <span className="text-blue-700">{String(value)}</span>;
    case "boolean": return <span className="text-purple-700">{String(value)}</span>;
    default:        return <span className="text-kfaint">{String(value)}</span>;
  }
}

export function JsonTreeNode({ name, value, depth, openDepth = 1 }: {
  name: string | null;
  value: unknown;
  depth: number;
  /** Nodes shallower than this start expanded. */
  openDepth?: number;
}) {
  const [open, setOpen] = useState(depth < openDepth);
  const isObj = value !== null && typeof value === "object";
  const label = name != null ? <span className="font-semibold text-ink">{name}: </span> : null;

  if (!isObj) {
    return <div className="leading-relaxed pl-3">{label}<JsonPrimitive value={value} /></div>;
  }

  const isArr = Array.isArray(value);
  const entries = isArr
    ? (value as unknown[]).map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>);
  const summary = isArr ? `[${entries.length}]` : `{${entries.length}}`;

  return (
    <div className="leading-relaxed">
      <button
        type="button"
        className="text-left hover:bg-paper rounded px-0.5 -mx-0.5"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="inline-block w-3 text-kfaint">{open ? "▾" : "▸"}</span>
        {label}
        <span className="text-kfaint">{summary}</span>
      </button>
      {open && (
        <div className="ml-1.5 border-l border-krule/60 pl-2 space-y-0.5">
          {entries.slice(0, JSON_TREE_CHILD_LIMIT).map(([k, v]) => (
            <JsonTreeNode key={k} name={k} value={v} depth={depth + 1} openDepth={openDepth} />
          ))}
          {entries.length > JSON_TREE_CHILD_LIMIT && (
            <div className="pl-3 text-kfaint italic">
              … {entries.length - JSON_TREE_CHILD_LIMIT} more entries — use the raw JSON view
            </div>
          )}
        </div>
      )}
    </div>
  );
}
